/**
 * CMS485Dialog - CMS 485 Home Health Certification Dialog
 * Extracted from ClientDetailPage - Complex clinical form
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Loader2 } from "lucide-react";
import { CMS485LengthNotice, FieldLengthHint } from "@/components/client-detail/CMS485LengthNotice";
import { SnippetPicker } from "@/components/client-detail/CMS485SnippetPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constants for CMS 485 form
const FUNCTIONAL_LIMIT_OPTIONS = [
  "Ambulation", "Bed Mobility", "Transfer", "Eating", "Toileting",
  "Bathing", "Dressing", "Grooming", "Vision", "Hearing",
  "Endurance", "Speech", "Legally Blind", "Dyspnea With"
];

const ACTIVITIES_PERMITTED_OPTIONS = [
  "Bedrest", "BRP", "Up as Tolerated", "Transfer Bed/Chair",
  "Exercise Prescribed", "Partial Weight Bearing", "Independent at Home",
  "Crutches", "Cane", "Wheelchair", "Walker", "No Restrictions", "Other"
];

const MENTAL_STATUS_OPTIONS = [
  "Oriented", "Comatose", "Forgetful", "Depressed", "Disoriented",
  "Lethargic", "Agitated", "Other"
];

// Keys must match the rows printed on the CMS-485 PDF (backend cms485.py task_labels)
const PCW_TASK_LABELS = {
  bathing: "Bathing",
  dressing_upper_body: "Dressing Upper Body",
  dressing_lower_body: "Dressing Lower Body",
  grooming: "Grooming",
  eating: "Eating",
  mobility_in_home: "Mobility in Home",
  toileting: "Toileting",
  transfers: "Transfers",
  meal_prep: "Meal Preparation",
  light_housekeeping: "Light Housekeeping",
  laundry: "Laundry",
  shopping: "Shopping/Errands",
  medication_reminders: "Medication Reminders",
};

const PCW_ASSISTANCE_LEVELS = [
  { value: "independent", label: "Independent" },
  { value: "supervision_cueing", label: "Supervision / Cueing" },
  { value: "constant_supervision_with_physical_intervention", label: "Constant Supervision with Physical Intervention" },
  { value: "partial_physical_assistance", label: "Partial Physical Assistance" },
  { value: "total_assistance", label: "Total Assistance" },
  { value: "na", label: "N/A" },
];

const PCW_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "bid", label: "Twice a Day (BID)" },
  { value: "tid", label: "Three Times a Day (TID)" },
  { value: "qid", label: "Four Times a Day (QID)" },
  { value: "every_visit", label: "Every Visit" },
  { value: "prn", label: "As Needed (PRN)" },
  { value: "na", label: "N/A" },
];

export function CMS485Dialog({
  open,
  onOpenChange,
  client,
  editingCms485Id,
  setEditingCms485Id,
  clearCms485Signatures,
  setClearCms485Signatures,
  cms485Form,
  setCms485Form,
  physicians,
  rnEmployees,
  savingCms485,
  onSave,
  // Use default options if not provided
  functionalLimitOptions = FUNCTIONAL_LIMIT_OPTIONS,
  activitiesPermittedOptions = ACTIVITIES_PERMITTED_OPTIONS,
  mentalStatusOptions = MENTAL_STATUS_OPTIONS,
  pcwTaskLabels = PCW_TASK_LABELS,
  pcwAssistanceLevels = PCW_ASSISTANCE_LEVELS,
  pcwFrequencyOptions = PCW_FREQUENCY_OPTIONS,
  clientId,
}) {
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/cms485/preview-pdf`, { client_id: clientId, ...cms485Form }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) toast.error("Pop-up blocked — allow pop-ups to preview the PDF");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ }
      }
      toast.error(typeof detail === "string" ? detail : "Could not render the preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setEditingCms485Id?.(null);
    setClearCms485Signatures?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        setEditingCms485Id?.(null);
        setClearCms485Signatures?.(false);
      }
    }}>
      <DialogContent className="max-w-[98vw] w-[98vw] sm:max-w-[95vw] h-[95vh] max-h-[95vh] overflow-y-auto p-6 sm:p-8">
        <DialogHeader>
          <DialogTitle>{editingCms485Id ? "Edit" : "Create"} CMS 485 - Home Health Certification</DialogTitle>
          <DialogDescription>
            {editingCms485Id ? "Update" : "Complete"} the Home Health Certification and Plan of Care form
            {clearCms485Signatures && (
              <span className="block mt-2 text-amber-600 font-medium">
                ⚠️ Existing signatures will be cleared when you save changes.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="sticky top-0 z-10 -mx-1 px-1 bg-white/95 backdrop-blur-sm">
          <CMS485LengthNotice form={cms485Form} />
        </div>
        <div className="space-y-6 py-4">
          {/* Certification Period */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Certification Period From *</Label>
              <Input
                type="date"
                value={cms485Form.certification_period_from}
                onChange={(e) => setCms485Form(prev => ({ ...prev, certification_period_from: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Certification Period To *</Label>
              <Input
                type="date"
                value={cms485Form.certification_period_to}
                onChange={(e) => setCms485Form(prev => ({ ...prev, certification_period_to: e.target.value }))}
              />
            </div>
          </div>

          {/* Diagnoses */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 border-b pb-2">Diagnoses</h4>
            <div className="space-y-2">
              <Label>Principal Diagnosis (ICD Code)</Label>
              <Input
                value={cms485Form.principal_diagnosis}
                onChange={(e) => setCms485Form(prev => ({ ...prev, principal_diagnosis: e.target.value }))}
                placeholder="e.g., I10 - Essential hypertension"
              />
            </div>
            <div className="space-y-2">
              <Label>Secondary Diagnoses</Label>
              <Textarea
                value={cms485Form.secondary_diagnosis}
                onChange={(e) => setCms485Form(prev => ({ ...prev, secondary_diagnosis: e.target.value }))}
                placeholder="List secondary diagnoses (one per line)"
                rows={3}
              />
              <FieldLengthHint value={cms485Form.secondary_diagnosis} />
            </div>
            <div className="space-y-2">
              <Label>Surgical Procedures</Label>
              <Input
                value={cms485Form.surgical_procedures}
                onChange={(e) => setCms485Form(prev => ({ ...prev, surgical_procedures: e.target.value }))}
                placeholder="Procedures relevant to plan of care"
              />
            </div>
            <div className="space-y-2">
              <Label>Other Diagnoses</Label>
              <Textarea
                value={cms485Form.other_diagnoses}
                onChange={(e) => setCms485Form(prev => ({ ...prev, other_diagnoses: e.target.value }))}
                placeholder="List other pertinent diagnoses"
                rows={2}
              />
              <FieldLengthHint value={cms485Form.other_diagnoses} />
            </div>
          </div>

          {/* DME and Safety */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 border-b pb-2">Equipment & Safety</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>DME & Supplies</Label>
                <SnippetPicker field="dme_supplies" label="DME & Supplies" value={cms485Form.dme_supplies} onInsert={(t) => setCms485Form((prev) => ({ ...prev, dme_supplies: t }))} />
              </div>
              <Textarea
                value={cms485Form.dme_supplies}
                onChange={(e) => setCms485Form(prev => ({ ...prev, dme_supplies: e.target.value }))}
                placeholder="List durable medical equipment and supplies"
                rows={2}
              />
              <FieldLengthHint value={cms485Form.dme_supplies} />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Safety Measures</Label>
                <SnippetPicker field="safety_measures" label="Safety Measures" value={cms485Form.safety_measures} onInsert={(t) => setCms485Form((prev) => ({ ...prev, safety_measures: t }))} />
              </div>
              <Textarea
                value={cms485Form.safety_measures}
                onChange={(e) => setCms485Form(prev => ({ ...prev, safety_measures: e.target.value }))}
                placeholder="Required safety measures"
                rows={2}
                data-testid="cms485-safety-measures"
              />
              <FieldLengthHint value={cms485Form.safety_measures} />
              {client?.has_seizure_history && (
                <p className="text-xs text-amber-700" data-testid="cms485-seizure-hint">Client has seizure history — "Seizure precautions" is added automatically and always prints on the 485.</p>
              )}
            </div>
          </div>

          {/* Nutritional & Allergies */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Nutritional Requirements</Label>
                <SnippetPicker field="nutritional_requirements" label="Nutrition" value={cms485Form.nutritional_requirements} onInsert={(t) => setCms485Form((prev) => ({ ...prev, nutritional_requirements: t }))} />
              </div>
              <Textarea
                value={cms485Form.nutritional_requirements}
                onChange={(e) => setCms485Form(prev => ({ ...prev, nutritional_requirements: e.target.value }))}
                placeholder="Special diet or nutritional needs"
                rows={2}
              />
              <FieldLengthHint value={cms485Form.nutritional_requirements} />
            </div>
            <div className="space-y-2">
              <Label>Allergies</Label>
              <Textarea
                value={cms485Form.allergies}
                onChange={(e) => setCms485Form(prev => ({ ...prev, allergies: e.target.value }))}
                placeholder="Known allergies"
                rows={2}
              />
            </div>
          </div>

          {/* Functional Limitations */}
          <div className="space-y-3">
            <Label>Functional Limitations</Label>
            <div className="flex flex-wrap gap-2">
              {functionalLimitOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={cms485Form.functional_limitations?.includes(option)}
                    onCheckedChange={(checked) => {
                      setCms485Form(prev => ({
                        ...prev,
                        functional_limitations: checked
                          ? [...(prev.functional_limitations || []), option]
                          : (prev.functional_limitations || []).filter(l => l !== option)
                      }));
                    }}
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Activities Permitted */}
          <div className="space-y-3">
            <Label>Activities Permitted</Label>
            <div className="flex flex-wrap gap-2">
              {activitiesPermittedOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={cms485Form.activities_permitted?.includes(option)}
                    onCheckedChange={(checked) => {
                      setCms485Form(prev => ({
                        ...prev,
                        activities_permitted: checked
                          ? [...(prev.activities_permitted || []), option]
                          : (prev.activities_permitted || []).filter(a => a !== option)
                      }));
                    }}
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Mental Status */}
          <div className="space-y-3">
            <Label>Mental Status</Label>
            <div className="flex flex-wrap gap-2">
              {mentalStatusOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={cms485Form.mental_status?.includes(option)}
                    onCheckedChange={(checked) => {
                      setCms485Form(prev => ({
                        ...prev,
                        mental_status: checked
                          ? [...(prev.mental_status || []), option]
                          : (prev.mental_status || []).filter(s => s !== option)
                      }));
                    }}
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Prognosis */}
          <div className="space-y-2">
            <Label>Prognosis</Label>
            <Select 
              value={cms485Form.prognosis} 
              onValueChange={(value) => setCms485Form(prev => ({ ...prev, prognosis: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="guarded">Guarded</SelectItem>
                <SelectItem value="poor">Poor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Orders and Goals */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 border-b pb-2">Orders & Goals</h4>
            
            {/* PCW Service Hours */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Orders for Discipline and Treatments</Label>
              
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <p className="text-sm text-slate-700 font-medium">
                  Personal Care Worker services to perform up to:
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Hours/Day</Label>
                    <Input
                      type="number"
                      min="0"
                      max="24"
                      value={cms485Form.pcw_hours_per_day}
                      onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_hours_per_day: e.target.value }))}
                      placeholder="Hours"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Days/Week</Label>
                    <Input
                      type="number"
                      min="0"
                      max="7"
                      value={cms485Form.pcw_days_per_week}
                      onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_days_per_week: e.target.value }))}
                      placeholder="Days"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Weeks</Label>
                    <Input
                      type="number"
                      min="0"
                      value={cms485Form.pcw_weeks}
                      onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_weeks: e.target.value }))}
                      placeholder="Weeks"
                    />
                  </div>
                </div>
                
                <div className="pt-2">
                  <p className="text-sm text-slate-700 font-medium mb-2">
                    PCW to perform cares which are included on the POC as follows:
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="7"
                      className="w-20"
                      value={cms485Form.pcw_poc_days_per_week}
                      onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_poc_days_per_week: e.target.value }))}
                      placeholder="Days"
                    />
                    <span className="text-sm text-slate-600">days/week</span>
                  </div>
                </div>
              </div>

              {/* PCW Tasks Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-700">Task</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-700">Assistance Level</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-700">Frequency</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {Object.entries(pcwTaskLabels).map(([taskKey, taskLabel]) => (
                      <tr key={taskKey} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{taskLabel}</td>
                        <td className="px-3 py-2">
                          <Select
                            value={cms485Form.pcw_tasks?.[taskKey]?.assistance || ""}
                            onValueChange={(value) => setCms485Form(prev => ({
                              ...prev,
                              pcw_tasks: {
                                ...(prev.pcw_tasks || {}),
                                [taskKey]: { ...(prev.pcw_tasks?.[taskKey] || {}), assistance: value }
                              }
                            }))}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select level" />
                            </SelectTrigger>
                            <SelectContent>
                              {pcwAssistanceLevels.map((level) => (
                                <SelectItem key={level.value} value={level.value}>
                                  {level.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">
                          <Select
                            value={cms485Form.pcw_tasks?.[taskKey]?.frequency || ""}
                            onValueChange={(value) => setCms485Form(prev => ({
                              ...prev,
                              pcw_tasks: {
                                ...(prev.pcw_tasks || {}),
                                [taskKey]: { ...(prev.pcw_tasks?.[taskKey] || {}), frequency: value }
                              }
                            }))}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select frequency" />
                            </SelectTrigger>
                            <SelectContent>
                              {pcwFrequencyOptions.map((freq) => (
                                <SelectItem key={freq.value} value={freq.value}>
                                  {freq.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* PRN Hours Checkbox */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="prn-hours"
                  checked={cms485Form.pcw_prn_hours}
                  onCheckedChange={(checked) => setCms485Form(prev => ({ ...prev, pcw_prn_hours: checked }))}
                />
                <Label htmlFor="prn-hours" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                  <span className="font-medium">PRN Hours</span> - 24 hours per year for Medicaid covered appointments and short duration episodes of acute care needs.
                </Label>
              </div>

              {/* MOT's */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                <Label>MOT&apos;s (Medically Oriented Tasks)</Label>
                <SnippetPicker field="pcw_mots" label="MOT's" value={cms485Form.pcw_mots} onInsert={(t) => setCms485Form((prev) => ({ ...prev, pcw_mots: t }))} />
              </div>
                <Textarea
                  value={cms485Form.pcw_mots}
                  onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_mots: e.target.value }))}
                  placeholder="Medically oriented tasks delegated to the PCW (e.g., medication reminders, glucose monitoring assistance, range of motion)..."
                  rows={3}
                />
              <FieldLengthHint value={cms485Form.pcw_mots} />
              </div>

              {/* Interventions/Parameters */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                <Label>Interventions/Parameters</Label>
                <SnippetPicker field="pcw_interventions_parameters" label="Interventions" value={cms485Form.pcw_interventions_parameters} onInsert={(t) => setCms485Form((prev) => ({ ...prev, pcw_interventions_parameters: t }))} />
              </div>
                <Textarea
                  value={cms485Form.pcw_interventions_parameters}
                  onChange={(e) => setCms485Form(prev => ({ ...prev, pcw_interventions_parameters: e.target.value }))}
                  placeholder="Specify detailed interventions, precautions, or specific parameters..."
                  rows={3}
                />
              <FieldLengthHint value={cms485Form.pcw_interventions_parameters} />
              </div>

              {/* Ordered Disciplines & Orders (Box 21 print order) */}
              <div className="space-y-2 col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center justify-between gap-2">
                <Label>Orders for Discipline & Treatments (numbered)</Label>
                <SnippetPicker field="orders_for_discipline" label="Orders" value={cms485Form.orders_for_discipline} onInsert={(t) => setCms485Form((prev) => ({ ...prev, orders_for_discipline: t }))} />
              </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCms485Form(prev => ({
                      ...prev,
                      orders_discipline_list: [...(prev.orders_discipline_list || []), ""]
                    }))}
                    data-testid="add-discipline-order-btn"
                  >
                    + Add Order
                  </Button>
                </div>
                <div className="space-y-2">
                  {(cms485Form.orders_discipline_list || []).length === 0 && (
                    <p className="text-xs text-slate-500 italic p-3 border border-dashed rounded-md text-center">
                      No orders yet. Click <b>Add Order</b> to build the numbered list — the order shown here is the order printed on Box 21 of the CMS-485.
                    </p>
                  )}
                  {(cms485Form.orders_discipline_list || []).map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2" data-testid={`discipline-order-row-${idx}`}>
                      <span className="mt-2 text-sm font-medium text-slate-500 w-6 text-right shrink-0">
                        {idx + 1}.
                      </span>
                      <Textarea
                        value={item}
                        onChange={(e) => {
                          const list = [...(cms485Form.orders_discipline_list || [])];
                          list[idx] = e.target.value;
                          setCms485Form(prev => ({ ...prev, orders_discipline_list: list }));
                        }}
                        placeholder="e.g., SN 1v/w x 9 wks for wound assessment, dressing change, medication management"
                        rows={2}
                        className="flex-1"
                      />
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button
                          type="button" size="icon" variant="outline"
                          className="h-7 w-7"
                          disabled={idx === 0}
                          onClick={() => {
                            const list = [...(cms485Form.orders_discipline_list || [])];
                            [list[idx - 1], list[idx]] = [list[idx], list[idx - 1]];
                            setCms485Form(prev => ({ ...prev, orders_discipline_list: list }));
                          }}
                          data-testid={`move-order-up-${idx}`}
                          title="Move up"
                        >
                          ↑
                        </Button>
                        <Button
                          type="button" size="icon" variant="outline"
                          className="h-7 w-7"
                          disabled={idx === (cms485Form.orders_discipline_list || []).length - 1}
                          onClick={() => {
                            const list = [...(cms485Form.orders_discipline_list || [])];
                            [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
                            setCms485Form(prev => ({ ...prev, orders_discipline_list: list }));
                          }}
                          data-testid={`move-order-down-${idx}`}
                          title="Move down"
                        >
                          ↓
                        </Button>
                        <Button
                          type="button" size="icon" variant="outline"
                          className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            const list = (cms485Form.orders_discipline_list || []).filter((_, i) => i !== idx);
                            setCms485Form(prev => ({ ...prev, orders_discipline_list: list }));
                          }}
                          data-testid={`remove-order-${idx}`}
                          title="Remove"
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Orders — free-text overflow / legacy notes */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Additional Orders / Notes</Label>
                <Textarea
                  value={cms485Form.orders_for_discipline}
                  onChange={(e) => setCms485Form(prev => ({ ...prev, orders_for_discipline: e.target.value }))}
                  placeholder="Any free-text notes that don't fit the numbered list above..."
                  rows={2}
                />
              <FieldLengthHint value={cms485Form.orders_for_discipline} />
                <p className="text-xs text-slate-500">
                  Prints after the numbered orders on Box 21.
                </p>
              </div>
            </div>

            {/* Goals */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Goals / Rehabilitation Potential</Label>
                <SnippetPicker field="goals" label="Goals" value={cms485Form.goals} onInsert={(t) => setCms485Form((prev) => ({ ...prev, goals: t }))} />
              </div>
              <Textarea
                value={cms485Form.goals}
                onChange={(e) => setCms485Form(prev => ({ ...prev, goals: e.target.value }))}
                placeholder="Patient goals and expected outcomes"
                rows={3}
              />
              <FieldLengthHint value={cms485Form.goals} />
            </div>
          </div>

          {/* Physician Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 border-b pb-2">Physician Information</h4>
            <div className="space-y-2">
              <Label>Select Physician</Label>
              <Select
                value={cms485Form.physician_id}
                onValueChange={(value) => {
                  const selected = physicians?.find(p => p.id === value);
                  // Build a clean multi-line address from the directory record
                  let addr = "";
                  if (selected) {
                    const bits = [];
                    if (selected.clinic_name || selected.practice_name) {
                      bits.push(selected.clinic_name || selected.practice_name);
                    }
                    if (selected.address) bits.push(selected.address);
                    const cityState = [selected.city, selected.state]
                      .filter(Boolean).join(", ");
                    const tail = [cityState, selected.zip_code].filter(Boolean).join(" ");
                    if (tail) bits.push(tail);
                    addr = bits.join("\n");
                  }
                  setCms485Form(prev => ({
                    ...prev,
                    physician_id: value,
                    physician_name: selected?.name || "",
                    physician_phone: selected?.phone || "",
                    physician_address: addr,
                  }));
                }}
              >
                <SelectTrigger data-testid="cms485-physician-select">
                  <SelectValue placeholder="Select a physician" />
                </SelectTrigger>
                <SelectContent>
                  {physicians?.map((physician) => (
                    <SelectItem key={physician.id} value={physician.id}>
                      {physician.name} {physician.npi ? `(NPI: ${physician.npi})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!physicians || physicians.length === 0) && (
                <p className="text-xs text-amber-600">No physicians found. Add physicians in the Physicians page first.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Physician Name</Label>
                <Input
                  value={cms485Form.physician_name}
                  onChange={(e) => setCms485Form(prev => ({ ...prev, physician_name: e.target.value }))}
                  placeholder="Certifying physician"
                />
              </div>
              <div className="space-y-2">
                <Label>Physician Phone</Label>
                <Input
                  value={cms485Form.physician_phone}
                  onChange={(e) => setCms485Form(prev => ({ ...prev, physician_phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Physician Address</Label>
              <Textarea
                value={cms485Form.physician_address}
                onChange={(e) => setCms485Form(prev => ({ ...prev, physician_address: e.target.value }))}
                placeholder="Full address (street, city, state, ZIP — clinic name on its own line if you want)"
                rows={3}
                data-testid="cms485-physician-address-input"
              />
              <p className="text-xs text-slate-500">
                Auto-fills from the physician directory when you pick a physician above. Prints in Box 24 on the CMS-485.
              </p>
            </div>
          </div>

          {/* Agency RN Signature */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 border-b pb-2">Agency RN (For Agency Signature)</h4>
            <div className="space-y-2">
              <Label>Select RN *</Label>
              <Select
                value={cms485Form.agency_rn_id}
                onValueChange={(value) => {
                  const selected = rnEmployees?.find(emp => emp.id === value);
                  setCms485Form(prev => ({
                    ...prev,
                    agency_rn_id: value,
                    agency_rn_name: selected?.name || ""
                  }));
                }}
              >
                <SelectTrigger data-testid="cms485-rn-select">
                  <SelectValue placeholder="Select an RN for agency signature" />
                </SelectTrigger>
                <SelectContent>
                  {rnEmployees?.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!rnEmployees || rnEmployees.length === 0) && (
                <p className="text-xs text-amber-600">No RN employees found. Add RN staff in the Employees page first.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>RN Name</Label>
              <Input
                value={cms485Form.agency_rn_name}
                onChange={(e) => setCms485Form(prev => ({ ...prev, agency_rn_name: e.target.value }))}
                placeholder="RN name for agency signature"
                disabled={!!cms485Form.agency_rn_id}
                className={cms485Form.agency_rn_id ? "bg-slate-50" : ""}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handlePreview} disabled={previewing} className="gap-1 mr-auto" data-testid="cms485-preview-pdf-btn">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
          </Button>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={onSave} 
            disabled={savingCms485}
            className={clearCms485Signatures ? "bg-amber-600 hover:bg-amber-700" : ""}
          >
            {savingCms485 
              ? "Saving..." 
              : editingCms485Id 
                ? (clearCms485Signatures ? "Update & Clear Signatures" : "Update CMS 485") 
                : "Create CMS 485"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
